import { useEffect, useRef } from 'react';
import type { WsEvent, WsEventMap } from '@threadline/contracts';
import { on, subscribeSession } from '@/lib/socket';

/**
 * Subscribe to a socket event for the lifetime of a component.
 *
 * The handler is held in a ref so a caller can pass an inline closure without
 * re-subscribing on every render - which would otherwise drop frames during
 * the gap between `off` and `on`.
 */
export function useLive<E extends WsEvent>(
  event: E,
  handler: (payload: WsEventMap[E]) => void,
  enabled = true,
): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return on(event, (payload) => ref.current(payload));
  }, [event, enabled]);
}

/** Joins the session room so per-session events start arriving. */
export function useSessionChannel(sessionId: string | null | undefined): void {
  useEffect(() => {
    if (!sessionId) return;
    return subscribeSession(sessionId);
  }, [sessionId]);
}
